<template>
  <CheckboxInput
    v-model="scoresStore.onlyKnownUsers"
    class="my-4 font-thin"
    :label="t('pages.wordle.only_known_users_checkbox')"
  ></CheckboxInput>
  <div class="flex items-center justify-between">
    <chevron-left-icon
      class="w-6 cursor-pointer text-gray-600"
      @click="decreaseScoreDate"
    ></chevron-left-icon>
    <h1
      class="text-center text-lg font-medium text-kekkle-dark dark:text-white"
    >
      {{ t("pages.wordle.statistics_off") }} <br />
      <span class="font-bold text-kekkle-dark dark:text-white">
        {{ scoreDate.toLocaleDateString() }}</span
      >
    </h1>
    <chevron-right-icon
      class="w-6 cursor-pointer text-gray-600"
      @click="increaseScoreDate"
    ></chevron-right-icon>
  </div>

  <spinner
    v-if="(fetchingScores || fetchingGameId) && !scores.length"
    class="mx-auto mt-16 w-8 animate-spin text-black-dark dark:text-white"
  ></spinner>

  <div
    v-else
    class="w-full rounded border-2 border-black-dark bg-white p-4 text-kekkle-dark shadow-teal-hard-sm dark:bg-kekkle-dark-light dark:text-white"
  >
    <div>
      <div
        v-for="(score, index) in scores"
        :key="score._id"
        class="relative mb-2 flex flex-nowrap items-center gap-2 p-2"
        :class="{
          'border-b border-black-dark':
            scores[index + 1]?.new_score !== score.new_score,
        }"
      >
        <div class="= flex grow flex-wrap items-center gap-6">
          <p class="text-3xl font-bold">
            <span
              v-if="authStore.authUuid === score.user_id"
              class="pointer text-xl"
              >👉</span
            >
            <span v-if="score.position === 1"> 🥇 </span>
            <span v-else-if="score.position === 2"> 🥈 </span>
            <span v-else-if="score.position === 3"> 🥉 </span>
            <span v-else> {{ score.position }}</span>
          </p>
          <div>
            <p class="">
              {{ t("pages.wordle.name") }}
              <span class="font-bold">{{ score.username }}</span>
            </p>
            <p v-if="groupMembers[score.user_id]" class="">
              {{ t("pages.wordle.nick_name") }}
              <span class="font-bold">{{
                groupMembers[score.user_id].name
              }}</span>
            </p>
            <p class="">
              {{ t("pages.wordle.score") }}
              <span class="font-bold">{{ score.new_score }}</span>
            </p>
          </div>
        </div>
        <mini-wordle-board
          :hide-letters="!hasPlayedTodaysGame && lookingAtNewestGameScore"
          :game-state="score.game_state"
        />
      </div>
      <p v-if="!scores.length">{{ t("pages.wordle.no_scores_known") }}</p>
    </div>
  </div>

  <DefaultButton
    v-if="scoresStore.hasMore && scores.length"
    class="mt-4"
    :loading="fetchingScores"
    @click="loadMore"
  >
    {{ t("pages.wordle.load_more") }}
  </DefaultButton>
</template>
<script setup>
import MiniWordleBoard from "@/components/wordle/WordleMiniBoard.vue";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/outline";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useWordleStore } from "@/stores/modules/wordle/wordle";
import { useWordleScoresStore } from "@/stores/modules/wordle/wordle-scores";
import { useWordleLeaderboardStore } from "@/stores/modules/wordle/wordle-leaderboard";
import { useWordleMonthlyScoresStore } from "@/stores/modules/wordle/wordle-monthly-scores";
import { computed, watch } from "vue";
import Spinner from "@/components/Spinner.vue";
import { useI18n } from "vue-i18n";
import CheckboxInput from "@/components/Inputs/CheckboxInput.vue";
import { watchDebounced } from "@vueuse/core";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";

const { t } = useI18n({ useScope: "global" });

const authStore = useAuthStore();
const groupsStore = useGroupsStore();
const wordleStore = useWordleStore();
const scoresStore = useWordleScoresStore();
const leaderboardStore = useWordleLeaderboardStore();
const monthlyScoresStore = useWordleMonthlyScoresStore();

if (wordleStore.matchDate) {
  scoresStore.setScoreDate(wordleStore.matchDate);
}
scoresStore.fetchScores(scoresStore.scoreDate);

const changeScoreDate = (days) => {
  let date = new Date(scoresStore.scoreDate);
  date.setDate(date.getDate() + days);
  scoresStore.setScoreDate(date);
};

function increaseScoreDate() {
  changeScoreDate(1);
}

function decreaseScoreDate() {
  changeScoreDate(-1);
}

watch(
  () => scoresStore.onlyKnownUsers,
  (newValue) => {
    scoresStore.fetchScores(scoresStore.scoreDate);
    leaderboardStore.setOnlyKnownUsersWorldeLeaderboard(newValue);
    scoresStore.setOnlyKnownUsersWordleScores(newValue);
    monthlyScoresStore.setOnlyKnownUsersWordleMonthlyScores(newValue);
  },
);

watchDebounced(
  () => scoresStore.scoreDate,
  () => {
    scoresStore.fetchScores(scoresStore.scoreDate);
  },
  { debounce: 250 },
);

const scores = computed(() => {
  let scores = scoresStore.currentScores;
  let position = 0;
  let lastScore = null;

  if (!scores || !scores.length) {
    return [];
  }

  // Create a copy to avoid mutating the store data
  const sortedScores = [...scores].sort((a, b) => b.new_score - a.new_score);

  return sortedScores.map((score) => {
    if (score.new_score !== lastScore) {
      position = position + 1;
      lastScore = score.new_score;
    }

    return {
      ...score,
      position: position,
    };
  });
});
const fetchingScores = computed(() => scoresStore.isFetchingScores);
const fetchingGameId = computed(() => scoresStore.isFetchingGameId);
const groupMembers = computed(() => groupsStore.activeGroupMembers);
const scoreDate = computed(() => scoresStore.scoreDate);

const hasPlayedTodaysGame = computed(() => wordleStore.hasFinished);
const lookingAtNewestGameScore = computed(() => {
  return (
    wordleStore.matchId === scoresStore.currentGameId ||
    (wordleStore.matchDate && wordleStore.matchDate < scoresStore.scoreDate)
  );
});

function loadMore() {
  scoresStore.fetchScores(scoresStore.scoreDate, true);
}
</script>
<style lang="scss">
.pointer {
  font-size: 28px;
}
</style>
